import React from 'react';
import './Certificate.css';
import CertificateBorder from './CertificateBorder';
import CertificateSeal from './CertificateSeal';
import CertificateQRCode from './CertificateQRCode';

const Certificate = ({
  studentName,
  courseName,
  issueDate,
  certificateId,
  founderName = "Mukesh Gautam",
  hrName = "Vandana Lokhman",
  startDate,
  endDate
}) => {
  const startFormatted = startDate
    ? new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'June 01, 2026';

  const endFormatted = endDate
    ? new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'July 15, 2026';

  return (
    <div id="certificate-download-area" className="certificate-container flex-shrink-0 mx-auto bg-white">
      {/* Abstract SVGs Borders and Backgrounds */}
      <div className="certificate-waves"></div>
      <CertificateBorder />

      {/* Main Content Layout Grid */}
      <div className="absolute inset-0 z-20 flex flex-col justify-between px-24 py-16 text-center">

        {/* Top Bar: Company Name (Left) & Cert Details (Right) */}
        <div className="w-full flex justify-between items-start mb-6 px-4">
          {/* Company Name (Left) */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center">
              <img src="/cc.png" alt="CCAI Logo" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <div className="text-left flex flex-col">
              <div className="text-[#6D4CFF] text-[24px] font-bold tracking-[0.1em] leading-none mb-1">
                CLIENTCODE AI
              </div>
              <div className="text-[#27B8FF] text-[9.5px] tracking-[0.14em] font-medium self-end pl-2">
                TECHNOLOGY THAT WORK
              </div>
            </div>
          </div>

          {/* Cert Details (Right) */}
          <div className="flex flex-col text-right text-[12px] text-[#1A2340] opacity-90">
            <div className="mb-1.5"><strong className="mr-2 text-[#6D4CFF]">Certificate ID:</strong> {certificateId || "CCAI-INT-2026-P9A5"}</div>
          </div>
        </div>

        {/* Header Region */}
        <div className="flex flex-col items-center mt-2">
          <h1
            className="text-[64px] font-[800] tracking-tight leading-none mb-2"
            style={{
              backgroundImage: 'linear-gradient(90deg, #6D4CFF, #2D8CFF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent'
            }}
          >
            CERTIFICATE
          </h1>
          <div className="text-[#1A2340] text-[24px] tracking-[0.15em] font-bold uppercase mb-8">
            OF COMPLETION
          </div>

          <div className="text-[#1A2340] text-[14px] tracking-[0.2em] font-medium uppercase opacity-80 flex items-center w-full justify-center">
            <div className="h-[1px] w-20 bg-[#1A2340] opacity-30 mr-6"></div>
            THIS IS TO CERTIFY THAT
            <div className="h-[1px] w-20 bg-[#1A2340] opacity-30 ml-6"></div>
          </div>
        </div>

        {/* Student Name Region */}
        <div className="flex flex-col items-center justify-center my-4">
          <h2 className="certificate-name text-[72px] text-[#1A2340] leading-tight px-12 pb-2 border-b-[1.5px] border-[#6D4CFF] border-opacity-40 min-w-[600px]">
            {studentName || "Pankaj Ram"}
          </h2>
        </div>

        {/* Description Region */}
        <div className="flex flex-col items-center px-16 mb-4">
          <p className="text-[#1A2340] text-[18px] mb-4">
            Has successfully completed the Internship Program in
          </p>
          <h3 className="text-[#6D4CFF] text-[32px] font-bold uppercase tracking-wider mb-2">
            {courseName || "BACKEND DEVELOPMENT"}
          </h3>
          <p className="text-[#1A2340] text-[16px] font-medium mb-6">
            from <span className="text-[#6D4CFF] font-bold">{startFormatted}</span> to <span className="text-[#6D4CFF] font-bold">{endFormatted}</span>
          </p>
          <p className="text-[#1A2340] opacity-80 text-[14px] max-w-[800px] leading-relaxed px-10">
            For demonstrating exceptional dedication, skill, and commitment throughout the duration of the internship.
          </p>
        </div>

        {/* Footer Layout (Signatures & Seal) */}
        <div className="w-full flex justify-center items-end mt-auto pb-4 z-30">

          {/* Signatures & Seal Center Block */}
          <div className="flex justify-center items-end px-2 gap-8">
            {/* Left Signature */}
            <div className="flex flex-col items-center justify-end">
              <div className="certificate-name text-[46px] text-[#1A2340] opacity-80 mb-2 transform -rotate-3 whitespace-nowrap">
                {founderName}
              </div>
              <div className="w-48 h-[1px] bg-[#E5E7EB] mb-2"></div>
              <div className="text-[#6D4CFF] font-bold text-[11px] tracking-widest uppercase mt-1 whitespace-nowrap">Founder & CEO</div>
            </div>

            {/* Center Seal */}
            <div className="flex justify-center pb-6 px-4">
              <CertificateSeal />
            </div>

            {/* Right Signature */}
            <div className="flex flex-col items-center justify-end">
              <div className="certificate-name text-[46px] text-[#1A2340] opacity-80 mb-2 transform -rotate-2 whitespace-nowrap">
                {hrName}
              </div>
              <div className="w-48 h-[1px] bg-[#E5E7EB] mb-2"></div>
              <div className="text-[#6D4CFF] font-bold text-[11px] tracking-widest uppercase mt-1 whitespace-nowrap">HR Manager</div>
            </div>
          </div>

        </div>

      </div>

      {/* QR Code (Bottom Right, absolutely positioned inside the borders) */}
      <div className="absolute bottom-[52px] right-[52px] z-30">
        <CertificateQRCode certificateId={certificateId} />
      </div>
    </div>
  );
};

export default Certificate;
